/**
 * Public read-only view of a tenant's organisation chart.
 *
 * Reached via `/org-chart-share/:token`. Anyone with the link can browse
 * the org chart without an account: zoom, pan, fit-to-screen and export
 * (PNG/PDF) are supported. The link is minted by a super_admin from the
 * authenticated OrgChart page and expires after N days.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Loader2,
  ShieldAlert,
  Sparkles,
  Clock,
  Network,
  ZoomIn,
  ZoomOut,
  Scan,
  ImageDown,
  FileText,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { OrgTree } from "../components/orgchart/OrgTree";
import { buildTree } from "./OrgChart";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 2;

function formatExpiry(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("tr-TR", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export default function PublicOrgChart() {
  const { token } = useParams();
  const [state, setState] = useState({
    loading: true,
    data: null,
    error: null,
  });
  const [zoom, setZoom] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const scrollRef = useRef(null);
  const wrapRef = useRef(null);
  const zoomRef = useRef(1);
  const panRef = useRef(null);
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/org-chart/share/${token}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || "Bağlantı geçersiz");
        }
        const data = await res.json();
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (err) {
        if (!cancelled)
          setState({
            loading: false,
            data: null,
            error: err.message || "Bir hata oluştu",
          });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const tree = useMemo(
    () => buildTree(state.data?.nodes || []),
    [state.data],
  );

  const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

  // "Sığdır" — scale so the whole tree fits inside the viewport.
  const fitToScreen = useCallback(() => {
    const box = scrollRef.current;
    const wrap = wrapRef.current;
    if (!box || !wrap) return;
    const cur = zoomRef.current || 1;
    const contentW = wrap.scrollWidth / cur;
    const contentH = wrap.scrollHeight / cur;
    if (!contentW || !contentH) return;
    const fit = Math.min(
      (box.clientWidth - 16) / contentW,
      (box.clientHeight - 16) / contentH,
      1,
    );
    setZoom(clampZoom(Math.floor(fit * 100) / 100 || 1));
    box.scrollTo({ left: 0, top: 0 });
  }, []);

  // Auto-fit once the chart is rendered (and re-fit on fullscreen toggle).
  useEffect(() => {
    if (state.loading || state.error) return undefined;
    const t = setTimeout(fitToScreen, 180);
    return () => clearTimeout(t);
  }, [state.loading, state.error, fullscreen, fitToScreen]);

  // Mouse-wheel zoom over the canvas.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const next = clampZoom(
        zoomRef.current * (e.deltaY < 0 ? 1.08 : 1 / 1.08),
      );
      setZoom(Math.round(next * 100) / 100);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [state.loading]);

  // Drag-to-pan.
  const onPanMouseDown = (e) => {
    if (e.button !== 0) return;
    const box = scrollRef.current;
    if (!box) return;
    panRef.current = {
      x: e.clientX,
      y: e.clientY,
      left: box.scrollLeft,
      top: box.scrollTop,
    };
    const onMove = (ev) => {
      const p = panRef.current;
      if (!p) return;
      box.scrollLeft = p.left - (ev.clientX - p.x);
      box.scrollTop = p.top - (ev.clientY - p.y);
    };
    const onUp = () => {
      panRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleExport = async (fmt) => {
    const el = wrapRef.current;
    if (!el || exporting) return;
    setExporting(true);
    const prevZoom = zoomRef.current;
    if (prevZoom !== 1) {
      setZoom(1);
      await new Promise((r) => setTimeout(r, 150));
    }
    try {
      const { exportOrgChartPdf, exportOrgChartPng } = await import(
        "../components/orgchart/exportOrgChart"
      );
      const label = state.data?.tenant?.name || "organizasyon-semasi";
      if (fmt === "pdf") await exportOrgChartPdf(el, label);
      else await exportOrgChartPng(el, label);
    } catch {
      // sessiz — public sayfada toast altyapısı yok
    } finally {
      setExporting(false);
      if (prevZoom !== 1) setZoom(prevZoom);
    }
  };

  const toolBtn =
    "flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-200 bg-white text-zinc-600 shadow-sm transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700 disabled:opacity-40";

  return (
    <div
      className={
        fullscreen
          ? "fixed inset-0 z-50 flex flex-col bg-white p-4"
          : "min-h-screen bg-gradient-to-br from-zinc-50 via-white to-violet-50 px-4 py-8"
      }
    >
      <div
        className={
          fullscreen
            ? "flex min-h-0 flex-1 flex-col"
            : "mx-auto w-full max-w-[1800px]"
        }
      >
        {!fullscreen && (
          <div className="mb-5 flex items-center gap-2 text-sm font-semibold text-zinc-500">
            <Sparkles className="h-4 w-4 text-violet-600" />
            <span className="tracking-wide">
              Serpix · Paylaşılan organizasyon şeması
            </span>
          </div>
        )}

        <div
          className={[
            "flex min-h-0 flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-xl",
            fullscreen ? "flex-1" : "",
          ].join(" ")}
        >
          {state.loading ? (
            <div className="flex flex-col items-center gap-3 p-16">
              <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
              <div className="text-sm text-zinc-500">
                Bağlantı doğrulanıyor…
              </div>
            </div>
          ) : state.error ? (
            <ErrorState message={state.error} />
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-100 bg-gradient-to-b from-zinc-50 to-white px-4 py-3 sm:px-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 text-violet-700">
                    <Network className="h-5 w-5" />
                  </div>
                  <div>
                    <div
                      className="text-base font-semibold text-zinc-900"
                      data-testid="public-org-chart-tenant"
                    >
                      {state.data.tenant?.name || "Organizasyon Şeması"}
                    </div>
                    <div className="text-[11px] text-zinc-500">
                      Organizasyon Şeması · yalnızca okuma
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() =>
                      setZoom((z) => clampZoom(Math.round((z - 0.1) * 100) / 100))
                    }
                    title="Uzaklaştır"
                    data-testid="public-org-zoom-out"
                    className={toolBtn}
                  >
                    <ZoomOut className="h-4 w-4" />
                  </button>
                  <span
                    data-testid="public-org-zoom-level"
                    className="w-12 text-center text-xs font-semibold tabular-nums text-zinc-600"
                  >
                    {Math.round(zoom * 100)}%
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setZoom((z) => clampZoom(Math.round((z + 0.1) * 100) / 100))
                    }
                    title="Yakınlaştır"
                    data-testid="public-org-zoom-in"
                    className={toolBtn}
                  >
                    <ZoomIn className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={fitToScreen}
                    title="Ekrana sığdır"
                    data-testid="public-org-zoom-fit"
                    className={toolBtn}
                  >
                    <Scan className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setZoom(1)}
                    title="%100"
                    data-testid="public-org-zoom-100"
                    className="flex h-8 items-center justify-center rounded-lg border border-zinc-200 bg-white px-2 text-[11px] font-semibold text-zinc-600 shadow-sm transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700"
                  >
                    100%
                  </button>
                  <span className="mx-1 h-5 w-px bg-zinc-200" />
                  <button
                    type="button"
                    onClick={() => handleExport("png")}
                    disabled={exporting}
                    title="PNG indir"
                    data-testid="public-org-export-png"
                    className="flex h-8 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-2.5 text-[11px] font-semibold text-zinc-600 shadow-sm transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700 disabled:opacity-40"
                  >
                    {exporting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <ImageDown className="h-3.5 w-3.5" />
                    )}
                    PNG
                  </button>
                  <button
                    type="button"
                    onClick={() => handleExport("pdf")}
                    disabled={exporting}
                    title="PDF indir"
                    data-testid="public-org-export-pdf"
                    className="flex h-8 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-2.5 text-[11px] font-semibold text-zinc-600 shadow-sm transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700 disabled:opacity-40"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    PDF
                  </button>
                  <span className="mx-1 h-5 w-px bg-zinc-200" />
                  <button
                    type="button"
                    onClick={() => setFullscreen((v) => !v)}
                    title={fullscreen ? "Tam ekrandan çık" : "Tam ekran"}
                    data-testid="public-org-fullscreen"
                    className={toolBtn}
                  >
                    {fullscreen ? (
                      <Minimize2 className="h-4 w-4" />
                    ) : (
                      <Maximize2 className="h-4 w-4" />
                    )}
                  </button>
                  <div className="ml-1 flex items-center gap-1.5 rounded-full bg-zinc-100 px-2.5 py-1 text-[11px] text-zinc-600">
                    <Clock className="h-3.5 w-3.5" />
                    <span>Bitiş: {formatExpiry(state.data.expires_at)}</span>
                  </div>
                </div>
              </div>

              <div
                ref={scrollRef}
                data-testid="public-org-chart-canvas"
                onMouseDown={onPanMouseDown}
                className={[
                  "min-h-0 cursor-grab select-none overflow-auto bg-gradient-to-br from-white via-white to-violet-50/40 active:cursor-grabbing",
                  fullscreen ? "flex-1" : "h-[calc(100vh-230px)] min-h-[420px]",
                ].join(" ")}
              >
                <div
                  ref={wrapRef}
                  data-testid="public-org-zoom-wrapper"
                  style={{ zoom }}
                  className="w-fit min-w-full p-8"
                >
                  {tree.length === 0 ? (
                    <div className="py-16 text-center text-sm text-zinc-500">
                      Bu şemada henüz düğüm yok.
                    </div>
                  ) : (
                    <OrgTree roots={tree} readOnly />
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {!fullscreen && (
          <p className="mt-5 text-center text-[11px] leading-relaxed text-zinc-400">
            Bu sayfa Serpix üzerinden oluşturulmuş, halka açık bir bağlantıdır.
            Süresi dolduğunda otomatik olarak devre dışı kalır.
          </p>
        )}
      </div>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div
      className="flex flex-col items-center gap-3 p-16 text-center"
      data-testid="public-org-chart-error"
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-rose-50 text-rose-600">
        <ShieldAlert className="h-7 w-7" />
      </div>
      <div className="text-base font-semibold text-zinc-900">
        Bağlantıya erişilemiyor
      </div>
      <div className="max-w-xs text-sm text-zinc-500">{message}</div>
    </div>
  );
}
